import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { WebhookEndpoint } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';

import { useRotateWebhookSecret } from '../hooks/use-webhook-mutations';

import { SecretPanel } from './secret-panel';

export interface RotateSecretDialogProps {
  storeId: string;
  endpoint: WebhookEndpoint | null;
  onOpenChange: (open: boolean) => void;
}

/** Asks first (the old secret stops working at once), then shows the new one once. */
export function RotateSecretDialog({ storeId, endpoint, onOpenChange }: RotateSecretDialogProps) {
  const { t } = useTranslation(['webhooks', 'common']);
  const message = useErrorMessage();
  const rotate = useRotateWebhookSecret(storeId);
  // Held in component state only; closing the dialog drops the secret for good.
  const [secret, setSecret] = useState<string | null>(null);

  const close = () => {
    onOpenChange(false);
    setSecret(null);
    rotate.reset();
  };

  return (
    <Dialog open={endpoint !== null} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent
        className="sm:max-w-lg"
        onInteractOutside={(e) => {
          if (secret) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {secret ? t('webhooks:secret.rotatedTitle') : t('webhooks:rotate.title')}
          </DialogTitle>
          <DialogDescription>
            {secret ? t('webhooks:secret.rotatedDescription') : t('webhooks:rotate.description')}
          </DialogDescription>
        </DialogHeader>
        {secret ? (
          <SecretPanel secret={secret} onDone={close} />
        ) : (
          <>
            {rotate.isError ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{message(rotate.error)}</AlertDescription>
              </Alert>
            ) : null}
            <p className="font-mono text-sm break-all">{endpoint?.url}</p>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={rotate.isPending}
                onClick={() => {
                  if (endpoint) {
                    rotate.mutate(endpoint.id, {
                      onSuccess: (rotated) => setSecret(rotated.secret),
                    });
                  }
                }}
              >
                {rotate.isPending ? t('webhooks:rotate.submitting') : t('webhooks:rotate.submit')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
